import { calcFutureValue } from '../utils/calcFutureValue';
import { isNullOrUndefined } from '../../isNullOrUndefined';
import { analyzeInvestmentReturns } from '../roi/calcProjectedROI';
import { Draft } from 'immer';
import {
  ReturnOnInvestmentCalculations,
  calculateMonthsBetweenYears,
} from './fundingTypes';

// ─────────────────────────────────────────────────────────────
// Per-account-type calculation configuration
// ─────────────────────────────────────────────────────────────

/**
 * Tax method applied when computing the after-tax projected value.
 *
 * `full-balance` — the entire balance/income is taxable (pre-tax contributions
 *   or ordinary income).
 * `gains-only`   — only the growth above cost basis is taxable (after-tax
 *   contributions or capital gains).
 */
type TaxMethod = 'full-balance' | 'gains-only';

/**
 * Calculation method used for projecting the funding source value.
 *
 * `investment`       — year-by-year simulation with compound growth, contributions,
 *   and withdrawals (used by calcSelfFundingProjectedCostCoverage).
 * `income-stream`    — non-compounding income stream over the care period
 *   (used by calcIncomeStreamProjectedCoverage).
 */
type CalcMethod = 'investment' | 'income-stream';

interface AccountTypeCalcConfig {
  calcMethod: CalcMethod;
  taxMethod: TaxMethod;
}

const DEFAULT_CALC_CONFIG: AccountTypeCalcConfig = {
  calcMethod: 'investment',
  taxMethod: 'gains-only',
};

const ACCOUNT_TYPE_CALC_CONFIG: Record<string, AccountTypeCalcConfig> = {
  traditionalIra:  { calcMethod: 'investment',     taxMethod: 'full-balance' },
  traditional401k: { calcMethod: 'investment',     taxMethod: 'full-balance' },
  rothIra:         { calcMethod: 'investment',     taxMethod: 'gains-only' },
  roth401k:        { calcMethod: 'investment',     taxMethod: 'gains-only' },
  hsa:             { calcMethod: 'investment',     taxMethod: 'gains-only' },
  brokerage:       { calcMethod: 'investment',     taxMethod: 'gains-only' },
  homeEquity:      { calcMethod: 'investment',     taxMethod: 'gains-only' },
  retirementIncome:{ calcMethod: 'income-stream',  taxMethod: 'full-balance' },
  pension:         { calcMethod: 'income-stream',  taxMethod: 'full-balance' },
  socialSecurity:  { calcMethod: 'income-stream',  taxMethod: 'full-balance' },
};

export function getCalcConfig(accountType: string | null | undefined): AccountTypeCalcConfig {
  if (!accountType) return DEFAULT_CALC_CONFIG;
  return ACCOUNT_TYPE_CALC_CONFIG[accountType] ?? DEFAULT_CALC_CONFIG;
}

export function isIncomeStreamAccountType(accountType: string | null | undefined): boolean {
  return getCalcConfig(accountType).calcMethod === 'income-stream';
}

export function calcSelfFundingProjectedCostCoverage(mutableClient: Draft<Client>) {
  const {
    fundingSources: {
      selfFunding,
      selfFunding: {
        contributionStartYear,
        monthlyIncome,
        existingAssetsValue,
        annualRateOfReturn,
        gainsTaxRate,
        monthlyContributionAmount,
      },
    },
    allPhaseCosts: { allPhaseCareMonthsNeeded },
    appliedInferenceSet,
  } = mutableClient;

  const ltcAtYear = appliedInferenceSet.ltcAtYear ?? selfFunding.contributionEndYear;
  // Use user-specified stop year if set, capped to not exceed LTC start
  selfFunding.contributionEndYear =
    selfFunding.contributionStopYear != null
      ? Math.min(selfFunding.contributionStopYear, ltcAtYear)
      : ltcAtYear;
  const contributionEndYear = selfFunding.contributionEndYear;

  selfFunding.contributionYearCount = contributionEndYear - contributionStartYear;
  selfFunding.contributionMonthCount =
    selfFunding.contributionYearCount > 0
      ? calculateMonthsBetweenYears(contributionStartYear, contributionEndYear)
      : 1;

  // Keep futureValueOfInitialInvestment for display and ROI calculations
  selfFunding.futureValueOfInitialInvestment =
    isNullOrUndefined(existingAssetsValue) || isNullOrUndefined(annualRateOfReturn)
      ? 0
      : calcFutureValue(existingAssetsValue, annualRateOfReturn, appliedInferenceSet.yearsTillLtc);

  // Year-by-year simulation: grow balance, add contributions, subtract withdrawals
  const rate = annualRateOfReturn ?? 0;
  const yearlyContribution = (monthlyContributionAmount ?? 0) * 12;

  // On-claim clients still need to record withdrawals for non-LTC expenses
  // (rent, food, etc.) during the care period. Default the drawdown end year
  // to the end of care rather than LTC-start, since "before LTC" is empty for
  // on-claim.
  const isOnClaim =
    appliedInferenceSet.yearsTillLtc != null && appliedInferenceSet.yearsTillLtc <= 0;
  const careEndYear = ltcAtYear + Math.round((allPhaseCareMonthsNeeded ?? 0) / 12);

  // Drawdown parameters — gather the user-set drawdown plus any in-memory
  // additional drawdowns layered on by callers (e.g. the guided-planning
  // wizard models policy-premium outflows as a recurring drawdown).
  const hasUserDrawdown = !!selfFunding.hasDrawdown && !!selfFunding.drawdownAmount;
  const userDrawdownStartYear = hasUserDrawdown
    ? (selfFunding.drawdownStartAtYear ?? new Date().getFullYear())
    : 0;
  const userDrawdownEndYear = hasUserDrawdown
    ? (selfFunding.drawdownEndAtYear ?? (isOnClaim ? careEndYear : ltcAtYear))
    : 0;
  const userAnnualDrawdown = hasUserDrawdown
    ? selfFunding.drawdownAmountInterval === 'yearly'
      ? selfFunding.drawdownAmount!
      : selfFunding.drawdownAmount! * 12
    : 0;

  const drawdowns: Drawdown[] = [];
  if (hasUserDrawdown) {
    drawdowns.push({
      startYear: userDrawdownStartYear,
      endYear: userDrawdownEndYear,
      annualAmount: userAnnualDrawdown,
    });
  }
  if (selfFunding.additionalDrawdowns?.length) {
    drawdowns.push(...selfFunding.additionalDrawdowns);
  }

  let balance = existingAssetsValue ?? 0;
  let totalContributed = existingAssetsValue ?? 0;
  let totalWithdrawn = 0;

  const yearsTillLtc = Math.max(0, appliedInferenceSet.yearsTillLtc);
  for (let i = 0; i < yearsTillLtc; i++) {
    const year = contributionStartYear + i;
    balance = balance * (1 + rate);

    if (year < contributionEndYear) {
      balance += yearlyContribution;
      totalContributed += yearlyContribution;
    }

    for (const dd of drawdowns) {
      if (year >= dd.startYear && year < dd.endYear) {
        balance -= dd.annualAmount;
        totalWithdrawn += dd.annualAmount;
      }
    }
  }

  // Apply any drawdown years that fall past the simulation loop (e.g. on-claim
  // clients with withdrawals during the care period — the loop above runs
  // zero iterations when yearsTillLtc <= 0).
  const loopEndYear = contributionStartYear + yearsTillLtc;
  for (const dd of drawdowns) {
    const effectiveStart = Math.max(dd.startYear, loopEndYear);
    if (dd.endYear > effectiveStart) {
      const yearsInRange = dd.endYear - effectiveStart;
      balance -= dd.annualAmount * yearsInRange;
      totalWithdrawn += dd.annualAmount * yearsInRange;
    }
  }

  // Set drawdown tracking fields — keep these tied to the user-set drawdown so
  // downstream display fields don't change shape when callers layer on
  // ephemeral additional drawdowns. drawdownTotalWithdrawn includes all.
  if (hasUserDrawdown) {
    selfFunding.drawdownStartYear = userDrawdownStartYear;
    selfFunding.drawdownEndYear = userDrawdownEndYear;
    selfFunding.drawdownTotalWithdrawn = totalWithdrawn;
  }

  // Determine taxable amount based on account type
  const { taxMethod } = getCalcConfig(selfFunding.accountType);
  const taxableAmount =
    taxMethod === 'full-balance'
      ? balance
      : Math.max(0, balance - Math.max(0, totalContributed - totalWithdrawn));
  const tax = taxableAmount * (gainsTaxRate ?? 0);
  const afterTaxBalance = Math.max(0, balance - tax);

  // calc total value of monthly income (gross — used for display and cost basis)
  selfFunding.monthlyIncomeTotal = allPhaseCareMonthsNeeded * (monthlyIncome ?? 0);
  const netMonthlyIncome =
    selfFunding.monthlyIncomeTotal * (1 - (selfFunding.monthlyIncomeTaxRate ?? 0));

  selfFunding.selfFundingTotalValue = afterTaxBalance + netMonthlyIncome;

  // Assume all funds can be used for LTC
  selfFunding.selfFundingProjectedCostCoverage = selfFunding.selfFundingTotalValue;
}

export function calcCapitalGainsTaxLiability(
  principal: number | null,
  gainsTaxRate: number | null,
  investmentFutureValue: number,
) {
  if (isNullOrUndefined(principal) || isNullOrUndefined(gainsTaxRate)) {
    return 0;
  }
  return (investmentFutureValue - principal) * gainsTaxRate;
}

export function calcFutureValueOfMonthlyContributions(
  monthlyContributionAmount: number,
  annualRateOfReturn: number,
  contributionMonthCount: number,
): number {
  if (
    isNullOrUndefined(monthlyContributionAmount) ||
    isNullOrUndefined(annualRateOfReturn) ||
    isNullOrUndefined(contributionMonthCount)
  ) {
    return 0;
  }
  if (annualRateOfReturn === 0) {
    return monthlyContributionAmount * contributionMonthCount;
  }

  return (
    monthlyContributionAmount *
    ((Math.pow(1 + annualRateOfReturn / 12, contributionMonthCount) - 1) /
      (annualRateOfReturn / 12))
  );
}

/**
 * Calculation for income-stream funding sources (e.g. retirement income,
 * Social Security, pension, rental income).
 *
 * `existingAssetsValue` stores the annual income amount at `incomeReferenceAge`
 * (defaulting to the client's current age). `annualRateOfReturn` is applied
 * each year as the income grows from the reference age through the end of the
 * care period.
 *
 * Formula (year-by-year):
 *   yearIncome_i = annualIncome × (1 + growthRate) ^ (yearsFromRefAge + i)
 *   grossTotal   = sum of yearIncome_i × (monthsInThatYear / 12) over care months
 *   netTotal     = grossTotal × (1 − incomeTaxRate)
 *
 * `gainsTaxRate` is reinterpreted as an income tax rate for this source type.
 */
export function calcIncomeStreamProjectedCoverage(mutableClient: Draft<Client>) {
  const {
    fundingSources: {
      selfFunding,
      selfFunding: { existingAssetsValue, gainsTaxRate, annualRateOfReturn, incomeReferenceAge },
    },
    allPhaseCosts: { allPhaseCareMonthsNeeded },
    appliedInferenceSet,
  } = mutableClient;

  const annualIncome = existingAssetsValue ?? 0;
  const incomeTaxRate = gainsTaxRate ?? 0;
  const growthRate = annualRateOfReturn ?? 0;
  const ltcAtAge = appliedInferenceSet?.ltcAtAge ?? null;
  // When the user hasn't set a reference age, fall back to LTC start age so
  // the entered amount is treated as already being in LTC-age dollars (no
  // growth applied by default).
  const referenceAge = incomeReferenceAge ?? ltcAtAge;
  const yearsToLtc =
    ltcAtAge != null && referenceAge != null ? ltcAtAge - referenceAge : 0;

  // Annual income projected to the start of the care period (used for display).
  const projectedAnnual =
    yearsToLtc !== 0 ? annualIncome * Math.pow(1 + growthRate, yearsToLtc) : annualIncome;
  const monthlyIncome = projectedAnnual / 12;

  // Year-by-year gross total across the care period, with growth compounding
  // each year from the reference age through the end of care.
  let grossTotal = 0;
  let remainingMonths = allPhaseCareMonthsNeeded;
  for (let i = 0; remainingMonths > 0 && i < 200; i++) {
    const annualThisYear = annualIncome * Math.pow(1 + growthRate, yearsToLtc + i);
    const monthsThisYear = Math.min(12, remainingMonths);
    grossTotal += (annualThisYear * monthsThisYear) / 12;
    remainingMonths -= monthsThisYear;
  }
  const netTotal = grossTotal * (1 - incomeTaxRate);

  selfFunding.monthlyIncome = monthlyIncome;
  selfFunding.monthlyIncomeTotal = grossTotal;
  selfFunding.selfFundingTotalValue = netTotal;
  selfFunding.selfFundingProjectedCostCoverage = netTotal;
  selfFunding.selfFundingTotalCost = grossTotal;
  selfFunding.selfFundingNonLtcPayout = 0;

  // No investment growth fields apply to income streams
  selfFunding.futureValueOfInitialInvestment = 0;
  selfFunding.contributionYearCount = 0;
  selfFunding.contributionMonthCount = 0;
  selfFunding.monthlyContributionAmount = null;

  // Total ROI reflects the tax drag: (netTotal - grossTotal) / grossTotal
  selfFunding.selfFundingProjectedReturnOnInvestment =
    grossTotal > 0 ? (netTotal - grossTotal) / grossTotal : 0;
  selfFunding.selfFundingProjectedCompoundAnnualGrowthRate = 0;
  selfFunding.selfFundingProjectedInternalRateOfReturn = 0;
  selfFunding.investmentPeriodYears = 0;
  selfFunding.investmentCashFlows = [];
  selfFunding.returnCashFlows = [];
}

export function calcSelfFundingTotalsOnClaim(mutableClient: Draft<Client>) {
  const {
    fundingSources: {
      selfFunding,
      selfFunding: { monthlyIncome, existingAssetsValue },
    },
    allPhaseCosts: { allPhaseCareMonthsNeeded },
  } = mutableClient;

  selfFunding.monthlyIncomeTotal = allPhaseCareMonthsNeeded * (monthlyIncome ?? 0);
  const netMonthlyIncome =
    selfFunding.monthlyIncomeTotal * (1 - (selfFunding.monthlyIncomeTaxRate ?? 0));

  // total value is equal to sum of existing assets and net monthly income
  selfFunding.selfFundingTotalValue = (existingAssetsValue ?? 0) + netMonthlyIncome;

  // set total cost and coverage equal to toal value
  selfFunding.selfFundingTotalCost = selfFunding.selfFundingTotalValue;
  selfFunding.selfFundingProjectedCostCoverage = selfFunding.selfFundingTotalValue;

  // assume that all funds can be used for LTC, so this is zero.
  selfFunding.selfFundingNonLtcPayout = 0;
}

export function calcSelfFundingTotalCost(mutableClient: Draft<Client>) {
  const {
    fundingSources: {
      selfFunding,
      selfFunding: {
        existingAssetsValue,
        monthlyContributionAmount,
        monthlyIncomeTotal,
        contributionMonthCount,
      },
    },
  } = mutableClient;
  if (
    isNullOrUndefined(existingAssetsValue) &&
    isNullOrUndefined(monthlyContributionAmount) &&
    isNullOrUndefined(monthlyIncomeTotal)
  ) {
    selfFunding.selfFundingTotalCost = undefined;
    return;
  }

  selfFunding.selfFundingTotalCost =
    (monthlyContributionAmount ?? 0) * contributionMonthCount +
    (existingAssetsValue ?? 0) +
    (monthlyIncomeTotal ?? 0);
}

export function calcSelfFundingProjectedROI(
  mutableClient: Draft<Client>,
  discountRate?: number,
): ReturnOnInvestmentCalculations {
  const {
    fundingSources: {
      selfFunding: { selfFundingTotalValue, selfFundingTotalCost },
      selfFunding,
    },
  } = mutableClient;

  if (isNullOrUndefined(selfFundingTotalValue) || !selfFundingTotalCost) {
    return {
      investments: [],
      returns: [],
      investmentPeriodYears: 0,
    };
  }

  // assume that all funds can be used for LTC, so this is zero.
  selfFunding.selfFundingNonLtcPayout = 0;

  const returnOnInvestment = (selfFundingTotalValue - selfFundingTotalCost) / selfFundingTotalCost;
  selfFunding.selfFundingProjectedReturnOnInvestment = returnOnInvestment;

  const investmentCashFlows = convertInvestmentCashFlows(mutableClient);
  const returnCashFlows = convertReturnCashFlows(mutableClient, selfFundingTotalValue);

  const { cagr, internalRateOfReturn, yearsToLastReturn, isIrrSuspect } =
    analyzeInvestmentReturns(investmentCashFlows, returnCashFlows, discountRate);

  selfFunding.selfFundingProjectedCompoundAnnualGrowthRate = cagr;
  selfFunding.selfFundingProjectedInternalRateOfReturn = internalRateOfReturn;
  selfFunding.isIrrSuspect = isIrrSuspect;
  selfFunding.investmentPeriodYears = yearsToLastReturn;
  selfFunding.investmentCashFlows = investmentCashFlows;
  selfFunding.returnCashFlows = returnCashFlows;
  return {
    investments: investmentCashFlows,
    returns: returnCashFlows,
    investmentPeriodYears: yearsToLastReturn,
  };
}

function convertInvestmentCashFlows(mutableClient: Draft<Client>): CashFlow[] {
  const {
    fundingSources: { selfFunding },
  } = mutableClient;
  const {
    existingAssetsValue,
    monthlyContributionAmount,
    contributionMonthCount,
    contributionStartYear,
  } = selfFunding;

  const baseYear = contributionStartYear ?? new Date().getFullYear();
  const cashFlows: CashFlow[] = [];

  if (!isNullOrUndefined(existingAssetsValue)) {
    cashFlows.push({
      year: baseYear,
      amount: existingAssetsValue,
    });
  }

  if (!isNullOrUndefined(monthlyContributionAmount)) {
    const fullYears = Math.floor(contributionMonthCount / 12) ?? 0;
    const remainingMonths = contributionMonthCount % 12;

    for (let year = 0; year < fullYears; year++) {
      cashFlows.push({
        year: baseYear + year,
        amount: monthlyContributionAmount * 12,
      });
    }

    if (remainingMonths > 0) {
      cashFlows.push({
        year: baseYear + fullYears,
        amount: monthlyContributionAmount * remainingMonths,
      });
    }
  }

  return cashFlows;
}

function convertReturnCashFlows(
  mutableClient: Draft<Client>,
  selfFundingTotalValue: number,
): CashFlow[] {
  const {
    allPhaseCosts: { allPhaseCareMonthsNeeded },
    fundingSources: {
      selfFunding: { monthlyIncomeTotal, contributionEndYear },
    },
    appliedInferenceSet: { ltcAtYear },
  } = mutableClient;

  const returnYears = Math.ceil(allPhaseCareMonthsNeeded / 12) ?? 1;
  const yearlyAmount = returnYears
    ? (selfFundingTotalValue - (monthlyIncomeTotal ?? 0)) / returnYears
    : 0;

  const careStartYear = ltcAtYear ?? contributionEndYear;

  return Array.from({ length: returnYears }, (_, index) => ({
    year: careStartYear + index,
    amount: yearlyAmount,
  }));
}
